import { Button, Divider, ListItemButton } from '@mui/material';
import React, { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';
import { fetchAllLeaguesBySport } from '../../../../../api/leagues';
import toast from 'react-hot-toast';
import { getAllCompetitionsByLeague, matchCompetitionsByAi, mergeCompetitions } from '../../../../../api/competitions';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Box } from '@mui/system';
import ConfigurationApiTable from '../configurationApiTable';
import ConfirmationDialog from '../../../../common/confirmationDialog';
import { MATCH_RESPONSE_STATUS } from '../../../../../utils/constants';
import timeout from '../../../../../utils/timeout';

const { v4: uuidv4 } = require('uuid');

const ConfigurationApiTournaments = ({ sport, sources, onClose, onSave }) => {
  const [leagueNames, setLeagueNames] = useState({});
  const [leaguesCollapseState, setLeaguesCollapseState] = useState({});
  const [competitionsHashBySource, setCompetitionsHashBySource] = useState({});
  const [competitionsHash, setCompetitionsHash] = useState({});
  const [apiConfigurationHash, setApiConfigurationHash] = useState({});
  const [aiMatchLoading, setAiMatchLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);

  const createCompetitionsHash = useCallback((leagues, competitions) => {
    const leagueNames = leagues.reduce((a, c) => {
      if (a[c.unionKey]) {
        a[c.unionKey] = `${a[c.unionKey]}/${c.name}`;
      } else {
        a[c.unionKey] = c.name;
      }
      return a;
    }, {});

    const leaguesCollapseState = leagues.reduce((a, c) => {
      a[c.unionKey] = false;
      return a;
    }, {});

    const competitionsHash = competitions.reduce((a, c) => {
      a[c.id] = c;
      return a;
    }, {});

    const competitionsHashBySource = competitions.reduce((a, c) => {
      if (!c.leagueUnionKey) {
        return a;
      }
      if (!a[c.leagueUnionKey]) {
        a[c.leagueUnionKey] = {};
      }
      if (!a[c.leagueUnionKey][c.source]) {
        a[c.leagueUnionKey][c.source] = {};
      }
      a[c.leagueUnionKey][c.source][c.id] = c;
      return a;
    }, {});

    const apiConfigurationHash = competitions.reduce((a, c) => {
      if (!c.leagueUnionKey) {
        return a;
      }
      if (!a[c.leagueUnionKey]) {
        a[c.leagueUnionKey] = {};
      }
      if (!c.unionKey) {
        return a;
      }
      if (a[c.leagueUnionKey][c.unionKey]) {
        a[c.leagueUnionKey][c.unionKey][c.source] = { id: c.id, source: c.source, unionKey: c.unionKey, unionType: c.unionType };
      } else {
        a[c.leagueUnionKey][c.unionKey] = { [c.source]: { id: c.id, source: c.source, unionKey: c.unionKey, unionType: c.unionType } };
      }
      sources.forEach((source) => {
        if (!a[c.leagueUnionKey][c.unionKey][source.id]) {
          a[c.leagueUnionKey][c.unionKey][source.id] = {
            id: null,
            source: source.id,
            unionKey: null,
            unionType: null,
          }
        }
      });
      return a;
    }, {});

    setLeagueNames(leagueNames);
    setLeaguesCollapseState(leaguesCollapseState);
    setCompetitionsHash(competitionsHash);
    setCompetitionsHashBySource(competitionsHashBySource);
    setApiConfigurationHash(apiConfigurationHash);
  }, [sources]);

  const setupCompetitions = useCallback(async () => {
    if (!sources) {
      return;
    }
    const sourceIdParams = sources.map((item) => ({ key: 'sources', value: item.id }));
    const params = [
      {
        key: "sportId",
        value: sport.id,
      },
      ...sourceIdParams,
    ];
    const [leagues, competitions] = await Promise.all([fetchAllLeaguesBySport(params), getAllCompetitionsByLeague(params)]);
    const sortedLeaguesBySource = [];
    sources.forEach((source) => sortedLeaguesBySource.push(...leagues.filter((item) => item.source === source.id)));
    createCompetitionsHash(sortedLeaguesBySource, competitions);
  }, [sport, sources, createCompetitionsHash]);

  const handleAddRow = useCallback((leagueUnionKey) => {
    const id = uuidv4();
    const items = {};
    sources.forEach((source) => items[source.id] = { id: null, source: source.id, unionKey: id, unionType: 'Manual' });
    setApiConfigurationHash({ ...apiConfigurationHash, [leagueUnionKey]: { ...apiConfigurationHash[leagueUnionKey], [id]: items }, });
  }, [apiConfigurationHash, sources]);

  const handleDeleteRow = useCallback((unionKey, leagueUnionKey) => {
    const configuration = { ...apiConfigurationHash, [leagueUnionKey]: { ...apiConfigurationHash[leagueUnionKey] } };
    delete configuration[leagueUnionKey][unionKey];
    setApiConfigurationHash(configuration);
  }, [apiConfigurationHash]);

  const handleChange = useCallback((leagueUnionKey, unionKey, source, competition) => {
    const updatedItem = { ...apiConfigurationHash[leagueUnionKey][unionKey][source], id: competition?.id || null, unionType: 'Manual' };
    delete updatedItem.accuracy;
    setApiConfigurationHash({
      ...apiConfigurationHash,
      [leagueUnionKey]: {
        ...apiConfigurationHash[leagueUnionKey],
        [unionKey]:
        {
          ...apiConfigurationHash[leagueUnionKey][unionKey],
          [source]: updatedItem
        }
      }
    });
  }, [apiConfigurationHash]);

  const handleSave = useCallback(async () => {
    const data = [];
    for (let leagueUnionKey in apiConfigurationHash) {
      for (let unionKey in apiConfigurationHash[leagueUnionKey]) {
        const currentData = apiConfigurationHash[leagueUnionKey][unionKey];
        const dataArray = [];
        for (let sourceKey in currentData) {
          const sourceData = currentData[sourceKey];
          if (sourceData.id !== null) {
            const { accuracy, ...cleanData } = sourceData;
            dataArray.push(cleanData);
          }
        }
        if (dataArray.length > 0) {
          data.push(dataArray);
        }
      }
    }
    await mergeCompetitions(sport.id, { items: data });
    toast.success('API Configuration saved successfully!');
    onSave();
  }, [apiConfigurationHash, sport.id, onSave]);

  const handleCollapse = useCallback((leagueId) => {
    setLeaguesCollapseState({ ...leaguesCollapseState, [leagueId]: !leaguesCollapseState[leagueId] });
  }, [leaguesCollapseState]);

  const validateAiRow = useCallback((row) => {
    if (row.items.length > sources.length) {
      return false;
    }
    const sourcesAmount = {};
    for (let i = 0; i < row.items.length; i++) {
      if (!competitionsHash[row.items[i].id]) {
        return false;
      }
      if (!sourcesAmount[row.items[i].source]) {
        sourcesAmount[row.items[i].source] = 1;
      } else {
        sourcesAmount[row.items[i].source] += 1;
      }
      if (sourcesAmount[row.items[i].source] > 1) {
        return false;
      }
    }
    return true;
  }, [competitionsHash, sources]);

  const handleAiMatchByLeague = useCallback(async (leagueUnionKey) => {
    const { status, matches } = await matchCompetitionsByAi(sport.id, { leagueUnionKey });
    if (status === MATCH_RESPONSE_STATUS.INPROGRESS) {
      await timeout(5000);
      return handleAiMatchByLeague(leagueUnionKey);
    }
    if (status === MATCH_RESPONSE_STATUS.FAILED) {
      return toast.error('Servers are busy. Try again later!');
    }
    const matchedRows = {};
    matches.forEach((row) => {
      const isValid = validateAiRow(row);
      if (!isValid) {
        return;
      }
      const id = uuidv4();
      matchedRows[id] = {};
      row.items.forEach((item) => {
        matchedRows[id][item.source] = {
          id: item.id,
          source: item.source,
          unionKey: null,
          unionType: 'Ai',
          accuracy: row.accuracy
        }
      });
      sources.forEach((source) => {
        if (!matchedRows[id][source.id]) {
          matchedRows[id][source.id] = {
            id: null,
            source: source.id,
            unionKey: null,
            unionType: null,
          }
        }
      });
    })
    setApiConfigurationHash({ ...apiConfigurationHash, [leagueUnionKey]: matchedRows });
  }, [apiConfigurationHash, sources, sport, validateAiRow]);

  const handleAiMatch = useCallback(async (leagueUnionKey) => {
    try {
      setAiMatchLoading(true);
      await handleAiMatchByLeague(leagueUnionKey);
    } finally {
      setAiMatchLoading(false);
    }
  }, [setAiMatchLoading, handleAiMatchByLeague]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setDialogData(null);
  }, []);

  const handleSaveClick = useCallback(() => {
    setIsDialogOpen(true);
    setDialogData({
      title: 'Confirm merge',
      content: 'Are you sure you want to save?',
      onConfirm: () => {
        handleSave();
        handleCloseDialog();
      },
      onClose: () => handleCloseDialog(),
    })
  }, [handleCloseDialog, handleSave]);

  const handleCloseClick = useCallback(() => {
    setIsDialogOpen(true);
    setDialogData({
      title: 'Confirm close',
      content: 'Are you sure you want to close? Changes won`t be saved.',
      onConfirm: () => {
        onClose();
        handleCloseDialog();
      },
      onClose: () => handleCloseDialog(),
    })
  }, [handleCloseDialog, onClose]);

  useEffect(() => {
    setupCompetitions();
  }, [setupCompetitions]);

  return (
    <Box>
      {dialogData && <ConfirmationDialog open={isDialogOpen} title={dialogData.title} onClose={dialogData.onClose} onConfirm={dialogData.onConfirm} content={dialogData.content} />}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '15px', justifyContent: 'space-between', height: 'calc(100vh - 275px)' }}>
        <Box sx={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
          {Object.keys(apiConfigurationHash).map((leagueUnionKey) =>
            <Box key={leagueUnionKey} sx={{ display: 'flex', flexDirection: 'column', padding: '0 0 15px' }}>
              <Divider />
              <ListItemButton
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
                onClick={() => handleCollapse(leagueUnionKey)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "25px" }}>
                  {leaguesCollapseState[leagueUnionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  {leagueNames[leagueUnionKey]}
                </div>
              </ListItemButton>
              {leaguesCollapseState[leagueUnionKey] &&
                <ConfigurationApiTable
                  sources={sources}
                  itemsHashBySource={competitionsHashBySource[leagueUnionKey]}
                  apiConfigurationHash={apiConfigurationHash[leagueUnionKey]}
                  onAiMatch={() => handleAiMatch(leagueUnionKey)}
                  onAddRow={() => handleAddRow(leagueUnionKey)}
                  onDeleteRow={(unionKey) => handleDeleteRow(unionKey, leagueUnionKey)}
                  onChange={(unionKey, source, league) => handleChange(leagueUnionKey, unionKey, source, league)}
                  autocompleteLabel={'Tournaments'}
                  maxHeight='calc(100vh - 450px)'
                  aiMatchLoading={aiMatchLoading}
                  showFilter
                />
              }
              {!leaguesCollapseState[leagueUnionKey] && <Divider />}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            {/* {<Button
            variant="contained"
            color={"info"}
            onClick={handleAiMatch}
          >
            AI Match
          </Button>} */}
            {<Button
              variant="contained"
              color={"success"}
              onClick={handleSaveClick}
            >
              Save
            </Button>}
          </Box>
          {<Button
            variant="contained"
            color={"error"}
            onClick={handleCloseClick}
          >
            Close
          </Button>}
        </Box>
      </Box>
    </Box>
  );
}

export default ConfigurationApiTournaments;

